<template>
  <div class="cart-page">
    <!-- 页面头部 -->
    <div class="page-header">
      <div class="back-btn" @click="goOrderMenu">
        <el-icon><ArrowLeft /></el-icon>
      </div>
      <h2>确认订单</h2>
      <div class="header-right">
        <div class="cart-count">
          共{{ cartStore.totalCount }}件商品
        </div>
      </div>
    </div>

    <!-- 购物车内容 -->
    <div class="cart-content">
      <div v-if="cartStore.totalCount === 0" class="empty-cart">
        <el-icon :size="80" color="#ccc">
          <ShoppingCart />
        </el-icon>
        <p class="empty-text">购物车空空如也</p>
        <p class="empty-tip">快去挑选喜欢的菜品吧</p>
        <el-button 
          type="primary" 
          plain 
          @click="goOrderMenu"
          class="continue-btn"
        >
          继续点餐
        </el-button>
      </div>

      <div v-else class="cart-items-container">
        <!-- 商品列表 -->
        <div class="cart-items">
          <div v-for="item in cartStore.items" :key="item.dishId" class="cart-item">
            <!-- 左侧图片 -->
            <div class="item-image">
              <DishImage :fileId="item.fileId" :alt="item.name"></DishImage>
            </div>

            <!-- 右侧详情 -->
            <div class="item-details">
              <!-- 第一行：菜名 -->
              <div class="item-name">{{ item.name }}</div>

              <!-- 第二行：单价、数量控制 -->
              <div class="item-bottom">
                <span class="item-price">¥{{ item.currentPrice.toFixed(1) }}</span>

                <div class="quantity-controls">
                  <el-button 
                    size="small" 
                    @click="updateCart(item, -1)"
                    class="quantity-btn"
                  >
                    <el-icon><Minus /></el-icon>
                  </el-button>
                  <span class="quantity">{{ item.quantity }}</span>
                  <el-button 
                    size="small" 
                    @click="updateCart(item, 1)"
                    class="quantity-btn"
                  >
                    <el-icon><Plus /></el-icon>
                  </el-button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 底部结算栏 -->
    <div class="bottom-bar" v-if="cartStore.totalCount > 0">
      <div class="bar-content">
        <div class="left-info">
          <div class="total-price" style="display: none;">¥{{ cartStore.totalPrice.toFixed(1) }}</div>
        </div>
        <div class="right-action">
          <el-button 
            type="primary" 
            size="large" 
            @click="goOrderMenu"
            class="bottom-btn"
          >
            继续加菜
          </el-button>
          <el-button 
            type="primary" 
            size="large" 
            @click="fnCheckout"
            class="bottom-btn"
          >
            现在下单
          </el-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { 
  ArrowLeft, ShoppingCart, Plus, Minus 
} from '@element-plus/icons-vue'
import { useCartStore } from '@/stores/common/cartStore'
import { useTableStore } from '@/stores/common/tableStore'
import { ElMessage } from 'element-plus'
import DishImage from '@/components/DishImage.vue'
import { idempotent } from '@/utils/idempotentKeyService'
import socketService from '@/utils/socketService'

const router = useRouter()
const cartStore = useCartStore()
const tableStore = useTableStore()

// 去点菜页面
const goOrderMenu = () => {
  router.push('/orderMenu')
}

// 提交订单
const fnCheckout = async () => {
  if (cartStore.items.length === 0) return
  const p = {
    tableNo : tableStore.tableNo,
    customerCnt : tableStore.customerCnt,
    startTime : tableStore.startTime
  }
  const res = await cartStore.checkout(p)
  // 清空购物车
  socketService.emit('cart-clear')
  tableStore.updateState(res)
  ElMessage.success('订单提交成功！正在为您准备菜品!')
  router.push('/orderComfirm')
}

const updateCart = (dish, quantity) => {
  socketService.emit('cart-updated', {
    dishId : dish.dishId
    ,name : dish.name
    ,categoryId : dish.categoryId
    ,currentPrice : dish.currentPrice
    ,fileId : dish.fileId
    ,quantity : quantity
  })
}

onMounted( () => {
  // 幂等性key取得
  idempotent.refresh()
})

onUnmounted( () => {
  // 幂等性key删除
  idempotent.clear()
})
</script>

<style scoped>
.cart-page {
  min-height: 100vh;
  background: #f5f5f5;
  display: flex;
  flex-direction: column;
}

/* 页面头部 */
.page-header {
  background: white;
  padding: 12px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  position: sticky;
  top: 0;
  z-index: 100;
}

.page-header h2 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #333;
  flex: 1;
  text-align: center;
}

.back-btn {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.back-btn .el-icon {
  font-size: 20px;
  color: #666;
}

.header-right {
  width: 40px;
}

.cart-count {
  font-size: 12px;
  color: #999;
  text-align: right;
}

/* 购物车内容 */
.cart-content {
  flex: 1;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 16px;
  padding-bottom: 80px; /* 为底部栏留出空间 */
}

/* 空购物车 */
.empty-cart {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 70vh;
  text-align: center;
}

.empty-text {
  margin-top: 20px;
  font-size: 16px;
  color: #666;
}

.empty-tip {
  margin-top: 8px;
  font-size: 14px;
  color: #999;
  margin-bottom: 30px;
}

.continue-btn {
  width: 200px;
  border-radius: 20px;
}

/* 购物车商品列表 */
.cart-items {
  background: white;
  border-radius: 12px;
  padding: 0 16px;
  margin-bottom: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.cart-item {
  padding: 16px 0;
  border-bottom: 1px solid #f0f0f0;
}

.cart-item:last-child {
  border-bottom: none;
}

/* 温馨提示 */
.tips-section {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 12px 16px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #666;
}

.tips-section .el-icon {
  color: #ff8e53;
  font-size: 16px;
}

/* 底部结算栏 */
.bottom-bar {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: white;
  padding: 12px 16px;
  border-top: 1px solid #f0f0f0;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.05);
  z-index: 100;
}

.bar-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.left-info {
  display: flex;
  flex-direction: column;
}

.total-price {
  font-size: 20px;
  font-weight: bold;
  color: #ff6b6b;
  line-height: 1.2;
}

.bottom-btn {
  min-width: 120px;
  border-radius: 20px;
  font-weight: 600;
  background: linear-gradient(135deg, #ff6b6b 0%, #ff8e53 100%);
  border: none;
  padding: 12px 24px;
  height: 48px;
}

/* 滚动条优化 */
.cart-content::-webkit-scrollbar {
  width: 4px;
}

.cart-content::-webkit-scrollbar-track {
  background: transparent;
}

.cart-content::-webkit-scrollbar-thumb {
  background: #ddd;
  border-radius: 2px;
}

.cart-content::-webkit-scrollbar-thumb:hover {
  background: #ccc;
}

/* 响应式调整 */
@media (max-width: 480px) {
  .page-header {
    padding: 12px;
  }
  
  .cart-content {
    padding: 12px;
    padding-bottom: 70px;
  }
  
  .bottom-bar {
    padding: 10px 12px;
  }
  
  .bottom-btn {
    padding: 10px 20px;
    font-size: 14px;
    min-width: 100px;
    height: 44px;
  }
}

/* 重写购物车商品项布局 */
.cart-item {
  display: flex;
  gap: 12px;
  padding: 12px 0;
  border-bottom: 1px solid #f0f0f0;
}

/* 左侧图片容器 */
.item-image {
  width: 60px;
  height: 60px;
  border-radius: 8px;
  overflow: hidden;
  flex-shrink: 0;
  background-color: #f5f5f5;
}
.item-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* 右侧详情容器 */
.item-details {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

/* 第一行菜名 */
.item-name {
  font-weight: 500;
  color: #333;
  margin-bottom: 8px;
  font-size: 16px;
}

/* 第二行（价格、数量控制、删除按钮） */
.item-bottom {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
}

/* 价格 */
.item-price {
  color: #ff6b6b;
  font-weight: 500;
  min-width: 60px;
  font-size: 16px;
}

/* 数量控制组 */
.quantity-controls {
  display: flex;
  align-items: center;
  gap: 8px;
}

.quantity-btn {
  width: 28px;
  height: 28px;
  padding: 0;
  border-radius: 50%;
  border: 1px solid #e0e0e0;
  background: white;
}
.quantity-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.quantity {
  min-width: 24px;
  text-align: center;
  font-weight: 500;
  font-size: 16px;
}
</style>